<script>
import { defineComponent, ref, watch } from 'vue'
import { useQuestionKeyboardNav } from '@/composables/useQuestionKeyboardNav'

export default defineComponent({
  name: 'TrackQuestion',
  props: {
    question: {
      type: Object,
      required: true
    },
    currentIndex: {
      type: Number,
      required: true
    },
    totalQuestions: {
      type: Number,
      required: true
    },
    previousAnswer: {
      type: Number,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: false
    },
    editing: {
      type: Boolean,
      default: false
    }
  },
  emits: ['answer', 'back', 'restart', 'cancel', 'delete'],
  setup(props, { emit }) {
    const selectedKey = ref(props.previousAnswer ?? null)

    watch(() => props.question.no, () => {
      selectedKey.value = props.previousAnswer ?? null
    }, { immediate: true })

    watch(() => props.previousAnswer, (newVal) => {
      selectedKey.value = newVal ?? null
    })

    function selectOption(key) {
      selectedKey.value = key
    }

    function submitAnswer() {
      if (selectedKey.value === null) return

      emit('answer', {
        questionNo: props.question.no,
        selectedKey: selectedKey.value
      })
    }

    const { keyHint } = useQuestionKeyboardNav({
      options: () => (props.question.answerOptions || []).map((o, i) => ({ key: i + 1, value: o.id })),
      onSelect: selectOption,
      onNext: submitAnswer,
      // ArrowLeft mirrors the Previous button, which the first question doesn't show.
      onPrev: () => { if (props.currentIndex > 0) emit('back') },
      canNext: () => selectedKey.value !== null,
      canNavigate: () => !props.editing,
      verb: 'select',
    })

    return {
      selectedKey,
      selectOption,
      submitAnswer,
      keyHint,
    }
  }
})
</script>

<template>
  <div class="lvl-eval">
    <div class="lvl-qcard">
      <div class="lvl-qcard-in">
        <!-- Question label -->
        <div class="lvl-q-eyebrow">Track selection</div>

        <!-- Question text -->
        <h2 class="lvl-q-title">{{ question.question }}</h2>

        <!-- Description -->
        <p v-if="question.description" class="lvl-q-desc">{{ question.description }}</p>

        <!-- Prompt -->
        <div class="lvl-trk-label">{{ question.prompt || 'Select one' }}</div>

        <!-- Answer options -->
        <div class="lvl-trk-opts" role="radiogroup">
          <button
            v-for="option in question.answerOptions"
            :key="option.id"
            type="button"
            role="radio"
            :aria-checked="selectedKey === option.id"
            class="lvl-trk-opt"
            :class="{ on: selectedKey === option.id }"
            @click="selectOption(option.id)"
            :data-testid="'track-option-' + option.name"
          >
            <span class="lvl-trk-radio" :class="{ on: selectedKey === option.id }"></span>
            <span class="lvl-trk-main">
              <span class="lvl-trk-name">{{ option.name }}</span>
              <span v-if="option.description && option.name !== option.description" class="lvl-trk-desc">{{ option.description }}</span>
            </span>
          </button>
        </div>
      </div>

      <!-- Editing the track from the summary: only Cancel / Update -->
      <div v-if="editing" class="lvl-cardfoot">
        <span class="restart"></span>
        <span class="prog">Editing the track · your other answers stay as they are</span>
        <span class="nav">
          <button type="button" class="lvl-btn lvl-btn--text" @click="$emit('cancel')">Cancel</button>
          <button type="button" class="lvl-btn" :disabled="selectedKey === null" @click="submitAnswer"><span class="ms">check</span> Update</button>
        </span>
      </div>
      <div v-else class="lvl-cardfoot">
        <span class="restart">
          <button type="button" class="lvl-btn lvl-btn--outlined" :disabled="readOnly" @click="$emit('restart')">Restart</button>
          <button v-if="canDelete" type="button" class="lvl-btn lvl-btn--danger" :disabled="readOnly" @click="$emit('delete')">Delete</button>
        </span>
        <span class="prog">question {{ currentIndex + 1 }} of {{ totalQuestions }}<template v-if="keyHint"> · {{ keyHint }}</template></span>
        <span class="nav">
          <!-- No Previous on the first question: once the evaluation has started
               the document and method are settled (Restart is the way back). -->
          <button v-if="currentIndex > 0" type="button" class="lvl-btn lvl-btn--outlined" @click="$emit('back')">Previous</button>
          <button type="button" class="lvl-btn" :disabled="selectedKey === null" @click="submitAnswer">Next <span class="ms">arrow_forward</span></button>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./evaluationTheme.scss"></style>
